/**
 * MobileTrainingLaunchDialog — mobile observation-unblock (v1.3 groundwork).
 *
 * Shown INSTEAD of the desktop `CompanionInstallModal` when a training launch
 * is attempted on a mobile-like runtime, where the Companion browser extension
 * cannot install, so the tokenless PING/PONG can never succeed. Without this,
 * mobile users hit the desktop "Install OptiBay Training Companion" dead-end
 * and the training URL never opens — which also blocks us from observing the
 * real mobile behaviour (does a YouTube training URL stay in Safari or deep-
 * link into the YouTube app?).
 *
 * INTEGRITY — this dialog deliberately does NOT:
 *   - mark the training complete,
 *   - mint a Companion session token,
 *   - create a monitored `training_consumption_sessions` row,
 *   - emit Companion / `completed_claimed_by_user` / verified evidence.
 * The actual launch is a plain `window.open` of the real provider URL,
 * performed by the consumer's `onOpenTraining` callback. Opening on mobile is
 * an EXPLICITLY UNVERIFIED launch; the copy states this plainly. Any credited
 * completion must still come from a later manual attestation or provider/LMS
 * callback — never from this screen.
 */
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ExternalLink } from 'lucide-react';

export interface MobileTrainingLaunchDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * Invoked synchronously on the primary click so the consumer's `window.open`
   * stays inside the user gesture (not popup-blocked). The consumer MUST NOT
   * create a monitored session or emit evidence from this callback.
   */
  onOpenTraining: () => void;
  /** Disable the primary button when the provider URL is missing/invalid. */
  canOpen?: boolean;
}

export function MobileTrainingLaunchDialog({
  open,
  onClose,
  onOpenTraining,
  canOpen = true,
}: MobileTrainingLaunchDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        data-testid="mobile-training-launch-dialog"
        className="sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle data-testid="mobile-training-launch-title">
            Training on mobile
          </DialogTitle>
          <DialogDescription data-testid="mobile-training-launch-body">
            Automatic Companion verification is not available on mobile yet. You
            can still open this training now, but OptiBay will mark it as opened
            on mobile — not verified. Your manager may ask you to confirm
            completion, or you can complete it later on a desktop with the
            OptiBay Companion for verified credit.
          </DialogDescription>
        </DialogHeader>

        <p
          className="text-xs text-muted-foreground"
          data-testid="mobile-training-launch-note"
        >
          Opening on mobile will not count as verified completion. If YouTube
          opens in the YouTube app instead of Safari, return to OptiBay and
          continue from there.
        </p>

        <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            data-testid="mobile-training-launch-later-button"
          >
            Do this later
          </Button>
          <Button
            type="button"
            onClick={onOpenTraining}
            disabled={!canOpen}
            aria-label="Open training on mobile"
            data-testid="mobile-training-launch-open-button"
          >
            <ExternalLink className="h-4 w-4 mr-2" aria-hidden="true" />
            Open training
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
